// Cloudflare Turnstile challenge used only when the backend enables it in
// /session/params. The public site key comes from that response; the secret key
// remains server-side and every token is validated there exactly once.

interface TurnstileApi {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      theme: 'auto';
      size: 'flexible';
      appearance: 'interaction-only';
      callback: (token: string) => void;
      'error-callback': (code: string) => void;
      'expired-callback': () => void;
      'timeout-callback': () => void;
    },
  ): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_ID = 'autospy-turnstile-api';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');
    const timeout = window.setTimeout(() => reject(new Error('Human verification failed to load')), 15_000);
    const finish = () => {
      window.clearTimeout(timeout);
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Human verification API is unavailable'));
    };
    const fail = () => {
      window.clearTimeout(timeout);
      scriptPromise = null;
      reject(new Error('Human verification failed to load'));
    };
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', fail, { once: true });
    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
  return scriptPromise;
}

/** Resolve one short-lived, single-use token for a session-init attempt. */
export async function getTurnstileToken(siteKey: string, action: string): Promise<string> {
  const api = await loadTurnstile();
  return new Promise<string>((resolve, reject) => {
    const overlay = document.createElement('div');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', 'Human verification');
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '2147483647',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: 'rgba(20, 20, 20, 0.72)',
    });
    const host = document.createElement('div');
    Object.assign(host.style, {
      width: 'min(100%, 360px)',
      minHeight: '65px',
      padding: '12px',
      borderRadius: '12px',
      background: '#272725',
    });
    overlay.appendChild(host);
    document.body.appendChild(overlay);

    let widgetId = '';
    let settled = false;
    const timeout = window.setTimeout(() => finish(new Error('Human verification timed out')), 60_000);
    const cleanup = () => {
      window.clearTimeout(timeout);
      if (widgetId) {
        try {
          api.remove(widgetId);
        } catch {
          // The widget may already have removed itself after an error.
        }
      }
      overlay.remove();
    };
    const finish = (error: Error | null, token = '') => {
      if (settled) return;
      settled = true;
      cleanup();
      if (error) reject(error);
      else resolve(token);
    };

    try {
      widgetId = api.render(host, {
        sitekey: siteKey,
        action,
        theme: 'auto',
        size: 'flexible',
        appearance: 'interaction-only',
        callback: (token) => finish(null, token),
        'error-callback': () => finish(new Error('Human verification failed')),
        'expired-callback': () => finish(new Error('Human verification expired')),
        'timeout-callback': () => finish(new Error('Human verification timed out')),
      });
    } catch (error) {
      finish(error instanceof Error ? error : new Error('Human verification failed'));
    }
  });
}
